/**
 * Iter 76 Phase 5 — Public Share Viewer.
 *
 * Rendered at /plan-share/:token (no auth). Talks ONLY to the public
 * /api/plan-share/{token}/* surface. Cannot access tenant APIs.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Lock, Download, FileText, ShieldCheck, ArrowLeft } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: true });

export default function PlanSharePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [gateInput, setGateInput] = useState({ password: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);

  const loadSheets = useCallback(async () => {
    try {
      const r = await pub.get(`/plan-share/${token}/sheets`);
      setSheets(r.data || []);
      if ((r.data || []).length > 0) setActiveSheet(r.data[0]);
    } catch { setSheets([]); }
  }, [token]);
  const loadMeta = useCallback(async () => {
    try {
      const r = await pub.get(`/plan-share/${token}`);
      setMeta(r.data);
      if (r.data?.gate_passed) loadSheets();
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 404 ? "This share link is no longer available." : "Unable to load share.");
    }
  }, [token, loadSheets]);
  useEffect(() => { if (token) loadMeta(); }, [token, loadMeta]);

  const submitGate = async () => {
    setSubmitting(true);
    try {
      await pub.post(`/plan-share/${token}/access`, {
        password: gateInput.password || null,
        email: gateInput.email || null,
      });
      await loadMeta();
      await loadSheets();
    } catch (e) {
      setError(e?.response?.data?.detail || "Access denied");
    } finally { setSubmitting(false); }
  };

  if (error && !meta) {
    return (
      <Shell>
        <div className="text-center py-24">
          <ShieldCheck size={48} className="mx-auto text-[var(--fop-text-3)] mb-4" />
          <div className="text-[var(--fop-text)] text-lg mb-1">{error}</div>
          <div className="text-[var(--fop-text-3)] text-sm">Contact the sender if you need continued access.</div>
        </div>
      </Shell>
    );
  }
  if (!meta) return <Shell><div className="py-16 text-[var(--fop-text-3)] text-sm">Loading…</div></Shell>;

  if (!meta.gate_passed) {
    return (
      <Shell>
        <div className="max-w-md mx-auto py-16">
          <div className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md p-6">
            <Lock size={20} className="text-[var(--fop-primary)] mb-3" />
            <div className="font-display text-[var(--fop-text)] text-xl mb-1">{meta.title}</div>
            {meta.description && <div className="text-sm text-[var(--fop-text-3)] mb-4">{meta.description}</div>}
            <div className="text-xs text-[var(--fop-text-3)] mb-3">
              This share is protected. {meta.access_mode === "password" ? "Enter the password to view." : "Enter the email this link was sent to."}
            </div>
            {meta.access_mode === "password" && (
              <input type="password" placeholder="Password" data-testid="share-gate-password"
                     value={gateInput.password} onChange={(e) => setGateInput((s) => ({ ...s, password: e.target.value }))}
                     className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-3 py-2 text-sm" />
            )}
            {meta.access_mode === "email_allowlist" && (
              <input type="email" placeholder="you@company.com" data-testid="share-gate-email"
                     value={gateInput.email} onChange={(e) => setGateInput((s) => ({ ...s, email: e.target.value }))}
                     className="w-full bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded px-3 py-2 text-sm" />
            )}
            {error && <div className="text-xs text-[var(--fop-danger)] mt-2" data-testid="share-gate-error">{error}</div>}
            <button onClick={submitGate} disabled={submitting} data-testid="share-gate-submit"
                    className="mt-4 w-full px-4 py-2 rounded bg-[var(--fop-primary)] text-black text-sm font-mono uppercase tracking-wider disabled:opacity-50"
                    style={{ minHeight: 44 }}>
              {submitting ? "Verifying…" : "Continue"}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="border-b border-[var(--fop-border)] px-4 py-3 flex items-center gap-3" data-testid="share-header">
        <div className="flex-1 min-w-0">
          <div className="font-display text-[var(--fop-text)] text-lg truncate">{meta.title}</div>
          {meta.description && <div className="text-xs text-[var(--fop-text-3)] truncate">{meta.description}</div>}
        </div>
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {meta.share_type} · {sheets.length} sheet{sheets.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex" style={{ height: "calc(100vh - 110px)" }}>
        <aside className="w-64 border-r border-[var(--fop-border)] overflow-y-auto" data-testid="share-sheet-list">
          {sheets.length === 0 ? (
            <div className="p-4 text-xs text-[var(--fop-text-3)]">No sheets in this share.</div>
          ) : (
            <ul>
              {sheets.map((s) => (
                <li key={s.sheet_id}>
                  <button onClick={() => setActiveSheet(s)}
                          data-testid={`share-sheet-row-${s.sheet_id}`}
                          className={`w-full text-left px-3 py-2 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] ${activeSheet?.sheet_id === s.sheet_id ? "bg-[var(--fop-surface-2)]" : ""}`}>
                    <div className="text-xs text-[var(--fop-text)] font-medium truncate">
                      {s.sheet_number} · {s.sheet_title}
                    </div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      {s.discipline}{s.revision ? ` · rev ${s.revision}` : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
        <main className="flex-1 overflow-hidden bg-[var(--fop-bg)]" data-testid="share-viewer">
          {activeSheet ? <ShareSheetView token={token} sheet={activeSheet} meta={meta} /> :
            <div className="p-8 text-sm text-[var(--fop-text-3)]">Select a sheet on the left.</div>}
        </main>
      </div>
    </Shell>
  );
}

function ShareSheetView({ token, sheet, meta }) {
  const [markups, setMarkups] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [objects, setObjects] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [sheet.sheet_id]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const safe = async (path, setter) => {
        try { const r = await pub.get(path); if (alive) setter(r.data || []); }
        catch { if (alive) setter([]); }
      };
      if (meta.include_markups) safe(`/plan-share/${token}/sheets/${sheet.sheet_id}/markups`, setMarkups);
      else setMarkups([]);
      if (meta.include_measurements) safe(`/plan-share/${token}/sheets/${sheet.sheet_id}/measurements`, setMeasurements);
      else setMeasurements([]);
      if (meta.include_objects) safe(`/plan-share/${token}/sheets/${sheet.sheet_id}/objects`, setObjects);
      else setObjects([]);
      // FP-028 (2026-09-09 audit remediation) -- include_photos/
      // include_documents were storable/toggleable switches with no
      // recipient-facing implementation at all.
      if (meta.include_photos) safe(`/plan-share/${token}/sheets/${sheet.sheet_id}/photos`, setPhotos);
      else setPhotos([]);
      if (meta.include_documents) safe(`/plan-share/${token}/sheets/${sheet.sheet_id}/documents`, setDocuments);
      else setDocuments([]);
    })();
    return () => { alive = false; };
  }, [token, sheet.sheet_id, meta.include_markups, meta.include_measurements, meta.include_objects, meta.include_photos, meta.include_documents]);

  const downloadUrl = useMemo(
    () => `${BACKEND}/api/plan-share/${token}/sheets/${sheet.sheet_id}/file`,
    [token, sheet.sheet_id]
  );
  // FP-027 (2026-09-09 audit remediation) -- this used to render a
  // static "FileText icon + filename" box no matter what: recipients
  // could never actually SEE the drawing, only metadata about it. This
  // now renders the real sheet raster (FP-025's fixed endpoint, does
  // NOT require allow_download -- viewing and downloading are
  // deliberately separate permissions) with markup/measurement
  // overlays drawn on top at their real normalized positions.
  const rasterUrl = useMemo(
    () => `${BACKEND}/api/plan-share/${token}/sheets/${sheet.sheet_id}/viewer_raster`,
    [token, sheet.sheet_id]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2 bg-[var(--fop-surface-2)]" data-testid="share-toolbar">
        <span className="font-display text-[var(--fop-text)] text-sm">{sheet.sheet_number}</span>
        <span className="text-[var(--fop-text-3)] text-xs truncate">· {sheet.sheet_title}</span>
        <div className="flex-1" />
        {meta.allow_download && (
          <a href={downloadUrl} data-testid="share-download"
             className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded bg-[var(--fop-primary)] text-black"
             style={{ minHeight: 36 }}>
            <Download size={14} /> Download PDF
          </a>
        )}
      </div>
      <div className="flex-1 grid grid-cols-3 gap-2 p-3 overflow-y-auto">
        <div className="col-span-2 bg-[var(--fop-surface-2)] border border-[var(--fop-border)] rounded p-3 flex items-start justify-center overflow-auto" data-testid="share-canvas">
          {imgError ? (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-[var(--fop-text-3)] mb-3" />
              <div className="text-[var(--fop-text)] text-base font-display">{sheet.sheet_number}</div>
              <div className="text-[var(--fop-text-3)] text-xs mb-4">{sheet.sheet_title}</div>
              <div className="text-xs text-[var(--fop-text-3)]" data-testid="share-canvas-noview">
                Drawing preview is not available for this sheet.
              </div>
            </div>
          ) : (
            <div className="relative inline-block max-w-full" data-testid="share-drawing-viewer">
              <img
                src={rasterUrl}
                alt={`${sheet.sheet_number} — ${sheet.sheet_title}`}
                className="max-w-full h-auto block select-none"
                onError={() => setImgError(true)}
                data-testid="share-drawing-image"
              />
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 1 1" preserveAspectRatio="none"
                data-testid="share-drawing-overlay"
              >
                {meta.include_markups && markups.map((m) => <MarkupShape key={m.id} m={m} />)}
                {meta.include_measurements && measurements.map((ms) => <MeasurementShape key={ms.id} m={ms} />)}
                {meta.include_objects && objects.map((o) => (
                  o.location ? (
                    <circle key={o.id} cx={o.location.nx} cy={o.location.ny} r={0.006}
                            fill="var(--fop-primary)" stroke="#000" strokeWidth={0.001} />
                  ) : null
                ))}
              </svg>
            </div>
          )}
        </div>
        <aside className="space-y-3">
          {meta.include_markups && (
            <SidePanel title={`Markups · ${markups.length}`} testid="share-markups" items={markups}
                        render={(m) => <Row key={m.id} primary={m.label || m.text || m.type} secondary={m.type} />} />
          )}
          {meta.include_measurements && (
            <SidePanel title={`Measurements · ${measurements.length}`} testid="share-measurements" items={measurements}
                        render={(m) => (
                          <Row key={m.id}
                               primary={m.label || `${m.kind}${m.value != null ? ` · ${m.value} ${m.unit || ""}` : ""}`}
                               secondary={<MeasurementTrustBadge m={m} />} />
                        )} />
          )}
          {meta.include_objects && (
            <SidePanel title={`Objects · ${objects.length}`} testid="share-objects" items={objects}
                        render={(o) => <Row key={o.id} primary={o.label} secondary={`${o.type}${o.status && o.status !== "active" ? ` · ${o.status}` : ""}`} />} />
          )}
          {meta.include_photos && (
            <SidePanel title={`Photos · ${photos.length}`} testid="share-photos" items={photos}
                        render={(p) => (
                          <li key={p.id} className="px-3 py-1.5 border-b border-[var(--fop-border)] last:border-0 flex items-center gap-2" data-testid={`share-photo-row-${p.id}`}>
                            <img src={`${BACKEND}/api/plan-share/${token}/photos/${p.id}/thumb`}
                                 alt={p.caption || p.origin_label || "Photo"}
                                 className="w-10 h-10 object-cover rounded border border-[var(--fop-border)] flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs text-[var(--fop-text)] truncate">{p.caption || p.origin_label || "Photo"}</div>
                              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{p.origin_type}</div>
                            </div>
                          </li>
                        )} />
          )}
          {meta.include_documents && (
            <SidePanel title={`Documents · ${documents.length}`} testid="share-documents" items={documents}
                        render={(d) => (
                          meta.allow_download ? (
                            <li key={d.id} className="border-b border-[var(--fop-border)] last:border-0">
                              <a href={`${BACKEND}/api/plan-share/${token}/documents/${d.id}/file`}
                                 data-testid={`share-document-row-${d.id}`}
                                 className="block px-3 py-1.5 hover:bg-[var(--fop-surface-2)]">
                                <div className="text-xs text-[var(--fop-text)] truncate">{d.title || d.file_name}</div>
                                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{d.document_type}</div>
                              </a>
                            </li>
                          ) : (
                            <Row key={d.id} primary={d.title || d.file_name} secondary={d.document_type} />
                          )
                        )} />
          )}
        </aside>
      </div>
    </div>
  );
}

// FP-026 (2026-09-09 audit remediation) -- renders a markup's REAL
// geometry (`points`, normalized 0..1) instead of nothing at all.
function MarkupShape({ m }) {
  const pts = m.points || [];
  if (pts.length === 0) return null;
  const d = pts.map((p) => `${p.nx},${p.ny}`).join(" ");
  const stroke = m.color || "#ff6a1a";
  const strokeWidth = ((m.stroke_width || 2) / 1000);
  if (m.type === "text" || pts.length === 1) {
    return <circle cx={pts[0].nx} cy={pts[0].ny} r={0.004} fill={stroke} />;
  }
  return (
    <polyline points={d} fill={m.fill || "none"} stroke={stroke}
              strokeWidth={strokeWidth || 0.002} vectorEffect="non-scaling-stroke" />
  );
}

// FP-026 -- renders a measurement's real geometry, and never presents
// an unverified/NTS value as if it were fully trusted (same principle
// FP-064 already enforces for exports).
function MeasurementShape({ m }) {
  const pts = m.points || [];
  if (pts.length === 0) return null;
  const untrusted = m.scale_trust === "untrusted" || m.scale_trust === "unverified";
  const stroke = untrusted ? "#f59e0b" : "#3b82f6";
  const d = pts.map((p) => `${p.nx},${p.ny}`).join(" ");
  return (
    <polyline points={d} fill="none" stroke={stroke}
              strokeDasharray={untrusted ? "0.01 0.01" : undefined}
              strokeWidth={0.0022} vectorEffect="non-scaling-stroke" />
  );
}

function MeasurementTrustBadge({ m }) {
  const untrusted = m.scale_trust === "untrusted" || m.scale_trust === "unverified"
    || m.requires_remeasurement || m.geometry_integrity_status === "legacy_unverified";
  return (
    <span className={untrusted ? "text-[var(--fop-warn,#f59e0b)]" : ""}>
      {m.kind}{untrusted ? " · UNVERIFIED — do not use for construction" : ""}
    </span>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--fop-bg)] text-[var(--fop-text)] flex flex-col" data-testid="share-shell">
      <header className="px-4 py-3 border-b border-[var(--fop-border)] flex items-center gap-2 bg-[var(--fop-surface)]">
        <div className="font-display text-base">ForgedOps Plans™ <span className="text-[var(--fop-text-3)] text-xs font-mono uppercase tracking-wider">· Secure Share</span></div>
        <div className="flex-1" />
        <a href="/" className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] flex items-center gap-1">
          <ArrowLeft size={12} /> forgedops.com
        </a>
      </header>
      {children}
      <footer className="px-4 py-3 border-t border-[var(--fop-border)] text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
        View-only · No tenant access · Recipient cannot mutate data
      </footer>
    </div>
  );
}
function SidePanel({ title, testid, items, render }) {
  // FP-029 (2026-09-09 audit remediation) -- previously the caller
  // hard-`.slice(0, 25)`'d the already-fully-fetched list and silently
  // dropped the remainder with no indication or way to see more, even
  // though the panel's own header correctly showed the TRUE total
  // count. Now progressively discloses the rest in batches of 25 using
  // data already in memory (no extra API calls) instead of a silent,
  // permanent cutoff.
  const [visible, setVisible] = useState(25);
  const shown = (items || []).slice(0, visible);
  const remaining = (items || []).length - shown.length;
  return (
    <div className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded" data-testid={testid}>
      <div className="px-3 py-2 border-b border-[var(--fop-border)] text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)]">{title}</div>
      <ul className="max-h-48 overflow-y-auto">
        {shown.length === 0 ? <Empty /> : shown.map(render)}
      </ul>
      {remaining > 0 && (
        <button
          onClick={() => setVisible((v) => v + 25)}
          data-testid={`${testid}-show-more`}
          className="w-full px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] border-t border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)]"
        >
          Show {Math.min(25, remaining)} more ({remaining} left)
        </button>
      )}
    </div>
  );
}
function Row({ primary, secondary }) {
  return (
    <li className="px-3 py-1.5 border-b border-[var(--fop-border)] last:border-0">
      <div className="text-xs text-[var(--fop-text)] truncate">{primary}</div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{secondary}</div>
    </li>
  );
}
function Empty() {
  return <li className="px-3 py-2 text-xs text-[var(--fop-text-3)]">None.</li>;
}
